import { Game, LobbyData, RingGame, RingGameFeature, SitAndGo, Status, Tournament } from './data/LobbyData'
import PokerLobbySessionManager from './PokerLobbySessionManager'
import { GameType } from './LobbyManager'
import { lobby } from '@vgw/pok-poker-lobby-client-lib'
import { FeatureToggleService } from './services/FeatureToggleService'
import proto = lobby.protocol

export default new (class {
	private allLobbyData: Map<proto.LobbyType, LobbyData> = new Map()

	constructor() {
		PokerLobbySessionManager.registerLobbyListEventHandler(this.handleEvent.bind(this))
	}

	private handleEvent(event: proto.LobbyListEvent) {
		if (event instanceof proto.LobbyListEvent.Subscribed) {
			this.setGames(event.lobby, event.games)
		} else if (event instanceof proto.LobbyListEvent.GameAdded) {
			this.addGame(event.lobby, event.game)
		} else if (event instanceof proto.LobbyListEvent.GameUpdated) {
			this.updateGame(event.lobby, event.update)
		} else if (event instanceof proto.LobbyListEvent.GameRemoved) {
			this.removeGame(event.lobby, event.gameId)
		}
	}

	private setGames(lobby: proto.LobbyType, games: proto.LobbyListGame[]) {
		this.allLobbyData.get(lobby)?.addItems(
			games.map((g) => {
				return mapFromLobbyListGame(g)
			}),
		)
	}

	private addGame(lobby: proto.LobbyType, game: proto.LobbyListGame) {
		this.allLobbyData.get(lobby)?.addItem(mapFromLobbyListGame(game))
	}

	private updateGame(lobby: proto.LobbyType, update: proto.LobbyListGame.Update) {
		this.allLobbyData.get(lobby)?.updateItems([mapFromGameUpdate(update)])
	}

	private removeGame(lobby: proto.LobbyType, gameId: string) {
		this.allLobbyData.get(lobby)?.remove(gameId)
	}

	initialize(sitAndGoLobbyData: LobbyData, ringGameLobbyData: LobbyData, scheduledTournamentLobbyData: LobbyData) {
		this.allLobbyData.set(proto.LobbyType.SitAndGo, sitAndGoLobbyData)
		if (FeatureToggleService.RingGamesFeature.includeNewPokerServerRingGames)
			this.allLobbyData.set(proto.LobbyType.RingGame, ringGameLobbyData)
		if (FeatureToggleService.ScheduledTournamentsFeature.includeNewPokerServerTournaments)
			this.allLobbyData.set(proto.LobbyType.Scheduled, scheduledTournamentLobbyData)
	}

	subscribeToLobbyList(gameType: GameType) {
		if (!FeatureToggleService.RingGamesFeature.includeNewPokerServerRingGames && gameType == GameType.Ring) return
		PokerLobbySessionManager.subscribeToLobbyList(gameType)
	}

	unsubscribeFromLobbyList(gameType: GameType) {
		if (!FeatureToggleService.RingGamesFeature.includeNewPokerServerRingGames && gameType == GameType.Ring) return
		PokerLobbySessionManager.unsubscribeFromLobbyList(gameType)
	}

	getGameName(gameId: string, gameType: GameType): string | undefined {
		return this.allLobbyData.get(mapToLobbyType(gameType)).getItem(gameId)?.name
	}
})

const mapToLobbyType = (from: GameType): proto.LobbyType => {
	switch (from) {
		case GameType.SitAndGo: return proto.LobbyType.SitAndGo
	}
}

const mapFromLobbyListGame = (from: proto.LobbyListGame): Game => {
	if (from instanceof proto.LobbyListGame.SitAndGo) {
		return {
			id: from.id,
			name: from.name,
			gameType: GameType.SitAndGo,
			capacity: from.capacity,
			ruleDescription: '',
			locked: false,
			type: mapFromBettingLimitMode(from.bettingLimitMode),
			variant: mapFromPokerVariant(from.pokerVariant),
			currencyCode: mapFromCoinType(from.coinType),
			buyIn: from.buyIn.toString(),
			fee: from.fee.toString(),
			registered: from.players,
			buyInCurrencyCode: mapFromCoinType(from.coinType),
			status: mapFromTournamentStatus(from.status),
		} as SitAndGo
	}
	if (from instanceof proto.LobbyListGame.RingGame) {
		return {
			id: from.id,
			gameType: GameType.Ring,
			capacity: from.seats,
			name: from.name,
			ruleDescription: '',
			locked: false,
			variant: mapFromPokerVariant(from.pokerVariant),
			currencyCode: mapFromCoinType(from.coinType),
			smallBlind: from.smallBlind.toString(),
			bigBlind: from.bigBlind.toString(),
			ante: from.ante,
			seated: from.players,
			level: 0,
			handsPerHour: from.handsPerHour,
			averagePot: from.averagePotSize.toString(),
			flopsSeen: from.percentageFlopsSeen,
			type: mapFromBettingLimitMode(from.bettingLimitMode),
			features: from.features?.map(feature => mapFromRingGameFeature(feature))
		} as RingGame
	}
	if (from instanceof proto.LobbyListGame.ScheduledTournament) {
		return {
			id: from.id,
			name: from.name,
			gameType: GameType.Tournament,
			capacity: from.capacity,
			ruleDescription: '',
			locked: false,
			type: mapFromBettingLimitMode(from.bettingLimitMode),
			variant: mapFromPokerVariant(from.pokerVariant),
			currencyCode: mapFromCoinType(from.coinType),
			buyIn: from.buyIn.toString(),
			fee: from.fee.toString(),
			registered: from.players,
			buyInCurrencyCode: mapFromCoinType(from.coinType),
			status: mapFromTournamentStatus(from.status),
			bountyFee: '0.00', // TODO: For bounty tournaments
			mysteryBountyFee: '0.00', // TODO: For mystery bounty tournaments
			registrationOpen: from.status === proto.TournamentStatus.Registering,
			startTime: from.scheduledStartTime.toString(),
		} as Tournament
	}
}

const mapFromBettingLimitMode = (from: proto.BettingLimitMode): string => {
	switch (from) {
		case proto.BettingLimitMode.NoLimit:
			return 'NO_LIMIT'
		case proto.BettingLimitMode.PotLimit:
			return 'POT_LIMIT'
		case proto.BettingLimitMode.FixedLimit:
			return 'FIXED_LIMIT'
	}
}

const mapFromPokerVariant = (from: proto.PokerVariant): string => {
	switch (from) {
		case proto.PokerVariant.Omaha:
			return 'OMAHA'
		case proto.PokerVariant.CrazyPineapple:
			return 'CRAZY_PINEAPPLE'
		case proto.PokerVariant.OmahaHighLow:
			return 'OMAHA_HL'
		case proto.PokerVariant.TexasHoldEm:
			return 'TEXAS_HOLDEM'
	}
}

const mapFromCoinType = (from: proto.CoinType): 'XSD' | 'XGC' | undefined => {
	switch (from) {
		case proto.CoinType.Gold:
			return 'XGC'
		case proto.CoinType.Sweeps:
			return 'XSD'
	}
}

const mapFromTournamentStatus = (from: proto.TournamentStatus): Status => {
	switch (from) {
		case proto.TournamentStatus.Announcing:
			return Status.ANNOUNCED
		case proto.TournamentStatus.Registering:
			return Status.REGISTERING
		case proto.TournamentStatus.Running:
			return Status.RUNNING
		case proto.TournamentStatus.Finished:
			return Status.FINISHED
		case proto.TournamentStatus.Cancelled:
			return Status.CANCELLED
		case proto.TournamentStatus.Closed:
			return Status.CLOSED
		case proto.TournamentStatus.OnBreak:
			return Status.ON_BREAK
		case proto.TournamentStatus.PreparingBreak:
			return Status.PREPARING_BREAK
	}
}

const mapFromRingGameFeature = (from: proto.RingGameFeature): RingGameFeature => {
	switch (from) {
		case proto.RingGameFeature.AllInOrFold:
			return RingGameFeature.AllInOrFold
		case proto.RingGameFeature.BadBeatJackpot:
			return RingGameFeature.BadBeatJackpot
	}
}

const mapFromGameUpdate = (from: proto.LobbyListGame.Update): any => {
	if (from instanceof proto.LobbyListGame.SitAndGo.Update) {
		return {
			id: from.id,
			status: mapFromTournamentStatus(from.status),
			registered: from.players,
		}
	}
	if (from instanceof proto.LobbyListGame.RingGame.Update) {
		return {
			id: from.id,
			seated: from.players,
			handsPerHour: from.handsPerHour,
			averagePot: from.averagePotSize,
			flopsSeen: from.percentageFlopsSeen,
			features: from.features?.map(feature => mapFromRingGameFeature(feature)),
		}
	}
	if (from instanceof proto.LobbyListGame.ScheduledTournament.Update) {
		return {
			id: from.id,
			status: mapFromTournamentStatus(from.status),
			registered: from.players,
			registrationOpen: from.registrationOpen,
		}
	}
}
